<script setup lang="ts">
import { Link } from '@inertiajs/vue3'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { FreeMode, Navigation } from 'swiper/modules'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/20/solid'
import { RelatedCatalogCategory } from '@interfaces/catalog-category.interface'

import 'swiper/css'
import 'swiper/css/free-mode'

defineProps<{
    subcategories: Array<RelatedCatalogCategory>,
}>()
</script>

<template>
    <div
        v-if="subcategories.length > 0"
        class="mt-10"
    >
        <h2 class="font-medium text-h6 text-grayscale-800">{{ $t('pages.catalog.subcategories') }}</h2>
        <div class="relative px-0 mt-4 sm:px-8 lg:px-10 2xl:px-0">
            <Swiper
                :modules="[FreeMode, Navigation]"
                :rewind="true"
                :free-mode="true"
                slides-per-view="auto"
                :slides-per-group-auto="true"
                :grab-cursor="true"
                :space-between="8"
                touch-events-target="container"
                :navigation="{
                    nextEl: '.subcategories-button-next',
                    prevEl: '.subcategories-button-prev',
                }"
                :breakpoints="{ 640: { spaceBetween: 12 }, 1024: { spaceBetween: 16 } }"
            >
                <SwiperSlide
                    v-for="subcategory in subcategories"
                    :key="subcategory.id"
                    class="box-content"
                >
                    <Link
                        class="text-center group"
                        :href="route('catalog', { catalog_category: subcategory.slug })"
                    >
                    <div class="relative flex items-center justify-center w-32 h-20 mx-auto overflow-hidden rounded-md">
                        <picture v-if="subcategory.image_url">
                            <source
                                :data-srcset="subcategory.image_webp_url"
                                type="image/webp"
                            >
                            <source
                                :data-srcset="subcategory.image_url"
                                type="image/jpeg"
                            >

                            <img
                                class="object-cover w-16 h-16 lazyload"
                                :data-src="subcategory.image_url"
                                :alt="subcategory.title"
                            >
                        </picture>


                        <div class="absolute inset-0 bg-black transition-colors bg-opacity-[0.02] group-hover:bg-opacity-[0.04]"></div>
                    </div>

                    <h3 class="mt-2 font-medium text-p2 text-grayscale-900">{{ subcategory.title }}</h3>
                    <span class="block text-p3 text-grayscale-500">{{ $t('pages.catalog.products-count', subcategory.products_count) }}</span>
                    </Link>
                </SwiperSlide>
            </Swiper>

            <div class="hidden sm:flex absolute top-5 -left-3.5 2xl:-left-14 z-10 items-center subcategories-button-next">
                <button
                    class="flex items-center justify-center text-white bg-gray-800 rounded-full w-9 h-9 lg:w-10 lg:h-10 hover:bg-gray-700 focus:outline-none"
                    aria-label="Previous"
                >
                    <ChevronLeftIcon class="w-6 h-6 -ml-0.5" />
                </button>
            </div>

            <div class="hidden sm:flex absolute top-5 -right-3.5 2xl:-right-14 z-10 items-center subcategories-button-prev">
                <button
                    class="flex items-center justify-center text-white bg-gray-800 rounded-full shadow w-9 h-9 lg:w-10 lg:h-10 hover:bg-gray-700 focus:outline-none"
                    aria-label="Next"
                >
                    <ChevronRightIcon class="w-6 h-6 -mr-0.5" />
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.swiper-slide {
    width: 128px;
}
</style>
